/**
 * useCachedGet — show the last figures at once, then bring them up to date
 * ========================================================================
 * Usability review of 23 October 2026, item 4: *"NAV / Performance sits on
 * "Computing NAV…" for about 5 seconds and Deal Reports on "Reading the
 * ledger…" for about 10. Show the last saved figures right away with an
 * "updating…" badge, or cache them."*
 *
 * The hook returns whatever this browser last read for the same request,
 * stamped with when it was read, and fetches again in the background. A page
 * shows `<FreshnessBadge>` beside the figures so a cached number is never
 * mistaken for a live one (CLAUDE.md §0.31: a stored figure is only worth the
 * engine that wrote it, so it says when it was written).
 *
 * The cache is a per-viewer convenience: it lives in memory for the session
 * and in localStorage across reloads, every access is wrapped, and the page
 * works the same when storage is unavailable or full. It is cleared on sign
 * out (AuthContext).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";

const PREFIX = "pms-cache:v1:";
// localStorage holds about five million characters per origin. Deal Reports'
// payload is about a million; anything much larger stays in memory only.
const MAX_CHARS = 2500000;
const memory = new Map();

export function cacheKey(url, params) {
  const query = params
    ? Object.keys(params).sort().map((k) => `${k}=${params[k]}`).join("&")
    : "";
  return `${url}${query ? `?${query}` : ""}`;
}

export function readCache(key) {
  if (memory.has(key)) return memory.get(key);
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry.savedAt !== "number") return null;
    memory.set(key, entry);
    return entry;
  } catch {
    return null;
  }
}

export function writeCache(key, data) {
  const entry = { savedAt: Date.now(), data };
  memory.set(key, entry);
  try {
    const raw = JSON.stringify(entry);
    if (raw.length <= MAX_CHARS) window.localStorage.setItem(PREFIX + key, raw);
  } catch {
    // Full or blocked storage: the memory copy is enough for this session.
  }
  return entry;
}

/** Forget every cached figure — on sign out, so the next person starts clean. */
export function clearCache() {
  memory.clear();
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // Nothing to clear.
  }
}

/**
 * `data` is the cached payload until the fetch lands, then the live one.
 * `loading` is true only when there is nothing at all to show yet;
 * `refreshing` is true while a fetch is in flight over figures already shown.
 * `savedAt` is when the figures on screen were read from the server.
 */
export default function useCachedGet(url, { params, enabled = true } = {}) {
  const key = url ? cacheKey(url, params) : null;
  const initial = key ? readCache(key) : null;
  const [entry, setEntry] = useState(initial);
  const [refreshing, setRefreshing] = useState(Boolean(key && enabled));
  const [error, setError] = useState("");
  const request = useRef(0);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const reload = useCallback(async () => {
    if (!key || !enabled) return;
    const id = ++request.current;
    setRefreshing(true);
    setError("");
    try {
      const res = await api.get(url, { params: paramsRef.current });
      if (id !== request.current) return;
      setEntry(writeCache(key, res.data));
    } catch (err) {
      if (id !== request.current) return;
      setError(err.response?.data?.error || err.response?.data?.detail || err.message || "Failed to load");
    } finally {
      if (id === request.current) setRefreshing(false);
    }
  }, [key, url, enabled]);

  // A new request shows its own cached copy at once, not the last request's.
  useEffect(() => {
    setEntry(key ? readCache(key) : null);
    reload();
  }, [key, reload]);

  return {
    data: entry ? entry.data : null,
    savedAt: entry ? entry.savedAt : null,
    loading: !entry && refreshing,
    refreshing,
    error,
    reload,
  };
}
